/**
 * Japanese (`ja`) string catalogue — mirrors the key shape of `./en.ts`.
 * Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`,
 * `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; the
 * rest translates idiomatically into terse, gamey Japanese using standard
 * tower-defense conventions (ウェーブ / アップグレード / 解放 / 難易度
 * etc.). UI strings stay tight to fit canvas-rendered chips.
 */

import type { Catalogue } from './en';

export const ja: Catalogue = {
  meta: {
    name: '日本語',
    code: 'JA',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'パルス',
      role: '中立な万能型。経路のどこでも安定したダメージ。',
    },
    arc: {
      label: 'Arc Coil',
      short: 'アーク',
      role: '電力網と連動。Pylon の余剰で強化、ブラウンアウトで弱体化。',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'クライオ',
      role: '隘路ペインター。範囲は狭い — 隘路の敵を減速し、もろさを刻印。',
    },
    mortar: {
      label: 'Splash Mortar',
      short: '迫撃',
      role: '間接射撃。経路から離して設置 — 近距離はデッドゾーン。',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'スナイパー',
      role: '直線貫通。長い直線では強力、コーナーでは弱い。',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: '電力供給。タワーの需要を賄える数を建設(Arc 用に余剰も)。',
    },
    wall: {
      label: 'Wall Node',
      short: '壁',
      role: '安価な阻塞 — Maze モード専用。敵の通り道を形作る。',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: 信頼の標準 — 経路のどこでも堅実なダメージ。',
    arc: 'Arc Coil: 電力余剰でスケール。Pylon を多めに建ててチェーンを強化。',
    cryo: 'Cryo Emitter: 隘路を狙う精密ペイント。挟撃ポイントを減速&マーク。',
    mortar: 'Splash Mortar: 間接射撃。経路から離して設置 — 近距離は当たらない。',
    sniper: 'Sniper Rail: 直線貫通。長い直線に揃えると効果最大。',
    pylon: 'Power Pylon: 電力網を支える。タワーの需要を賄うだけ建てよう。',
    wall: '壁: 安価な阻塞 — 敵の通り道を形作る。',
  },

  hints: {
    build:
      'タワーを建設:下から選び(または 1)、経路際の光るタイルをクリック。',
    placed: '良し。もう一つ建てて、スペースでウェーブ開始。',
    wave1: 'タワーは自動で発射。電力バーを赤に入れないよう注意。',
    select:
      '設置済みタワーをクリックで選択 — U でアップグレード、S で売却。',
    brownout:
      'ブラウンアウト!タワー減速。Pylon(3)を建てて電力を補充。',
  },

  contextHints: {
    overload:
      'オーバーロード:次ウェーブを上げて報酬アップ — リスクも上昇。出だしが遅い?捻ってFlux加速。',
    profileKey:
      'P でプロフィールを開く — Flux で新タワー・スキル・モードを解放。',
    endlessMod:
      'Endless はウェーブ MOD をローテーション — 構成を合わせよう。',
    puzzleHover:
      'パズルモード:手作りのロジックステージ、視界遮断壁あり。まったく別ゲーム。',
  },

  hud: {
    buildZone: '建設',
    waveZone: 'ウェーブ',
    abilitiesZone: 'スキル',
    overloadZone: 'オーバーロード',

    startWave: 'ウェーブ開始',
    nextWave: '次のウェーブ',
    pause: '一時停止',
    resume: '再開',
    restart: 'リスタート',
    deployHint: '出撃 [SPACE]',
    holdHint: '停止 [SPACE]',
    rebootHint: '再起動 [R]',
    nextWaveHint: '次のウェーブ',

    audioOn: '音声 ON',
    audioOff: '音声 OFF',

    credits: 'クレジット',
    core: 'コア',
    wave: 'ウェーブ',
    endless: 'ENDLESS',
    score: 'スコア',
    powerGrid: '電力網',

    runMode: 'モード',
    difficulty: '難易度',
    profile: 'プロフィール',
    map: 'マップ',
    clickHint: '▷ クリック',

    locked: 'ロック',
    lockedShort: 'ロック',
    lockedTooltip:
      'ロック — プロフィール画面で ✦{cost} Flux で解放(P)。',
    lockedTowerNotice: '{tower} はロック中',
    lockedAbility: 'スキルはロック中',
    lockedOverload: 'オーバーロード段階ロック中',
    lockedMode: '{mode} モードはロック中',
    lockedDifficulty: 'Hard 難易度はロック中',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— オーバーロードは第1ウェーブから適用 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ 発動中 {sec}s',

    maxLevel: '最大Lv',
    upgrade: 'アップグレード L{level}',
    sell: '売却',
    target: 'ターゲット: {mode}',
    cycleTarget: '切替 · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'オーバークロック',
    overclockOn: 'オーバークロック ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "ビルドフェーズ  //  タワー選択 [1·2·3·4·5] 配置後 START WAVE [SPACE]",
    buildHintReinforce: "ビルドフェーズ  //  グリッドを強化し START WAVE {wave} [SPACE]",
    mazeRoute: "迷 路 ル ー ト",
    tiles: "タイル",
    abilityActive: "◉ アクティブ {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} オーバークロック ON",
    overclockNoticeOff: "{tower} オーバークロック OFF",

    tierMult: {
      hp: "HP",
      sp: "速",
      cn: "数",
      gd: "金",
      sc: "点",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "カーネル",
    },
  },

  modes: {
    campaign: 'キャンペーン',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'パズル',
  },
  modeSub: {
    campaign: '10ウェーブ · 勝利',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'ステージ選択',
  },
  modeBlurb: {
    campaign: '10ウェーブ。波10でボス。勝てば卒業。',
    endless: '同じマップ。毎波モディファイア。順応か、漏らすか。',
    maze: 'オープンフィールド。壁で経路を作る。減速か、報酬を削れ。',
    puzzle: '10手作りステージ。唯一の勝利配置を見つけよ。',
  },
  modeCard: {
    campaign: {
      tagline: 'グリッドを学び、ボスを倒せ。',
      bullet1: '手調整10ウェーブ + ボス',
      bullet2: '固定S字経路 — 建てる、波、強化',
      bullet3: '勝利でこのマップのEndlessを解放',
      length: '約8分',
      skill: '建造順を鍛える',
    },
    endless: {
      tagline: '順応か漏らすか。終わりは無い。',
      bullet1: 'Campaign 1–10、その後ずっと拡大',
      bullet2: '毎波で新しいモディファイア',
      bullet3: 'コア陥落で終了',
      length: '約30分',
      skill: '長期の方針転換を鍛える',
    },
    maze: {
      tagline: '敵が歩く迷路を君が作る。',
      bullet1: 'オープン場 — 描かれた経路なし',
      bullet2: 'タワー+安い壁で経路を形成',
      bullet3: '経路が長いほど射撃時間が伸びる',
      length: '約12分',
      skill: '空間ルーティングを鍛える',
    },
    puzzle: {
      tagline: '勝つ配置はひとつ。それを見つけよ。',
      bullet1: '10ステージ、唯一解',
      bullet2: '予算固定・ウェーブ固定',
      bullet3: '視線遮断壁+タワー上限',
      length: '各ステージ約2分',
      skill: '推理を鍛える',
    },
  },
  difficulty: {
    easy: 'EASY',
    normal: 'NORMAL',
    hard: 'HARD',
  },
  difficultySub: {
    easy: 'やさしい',
    normal: 'ふつう',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: '一時停止',
    pauseHint: 'スペース で 再開',

    missionComplete: '// ミッション完了',
    gridSecured: '電力網確保',
    wavesRepelled: '{total} 波の襲撃をすべて撃退。',
    continueEndless: '続行  →  ENDLESS',
    unlockEndless: 'プロフィールで Endless を解放して続行',
    restartCampaign: 'または R でキャンペーン再開',

    systemFailure: '// システム障害',
    coreBreached: 'コア突破',
    endlessEnded: 'Endless 走行は第 {wave} 波で終了。',
    reactorOverrun: 'リアクター陥落:第 {wave} / {total} 波。',
    runItBack: 'R で再挑戦',

    finalScore: '最終スコア',
    newHighScore: '★  ハイスコア更新  ★',
    newEndlessBest: '★  ENDLESS 自己ベスト更新  ★',
    highScore: 'ハイスコア',
    endlessBest: 'ENDLESS ベスト',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// 余震 — グリッド返済  {sec}s //",
    overcharge: "// OVERCHARGE — グリッドブースト  {sec}s //",
    brownout: "// グリッドダウン — タワー減速 //",
  },

  profile: {
    title: 'プロフィール',
    subtitle: '// メタ進捗',
    closeHint: 'P または ESC で閉じる',
    tabUnlocks: '解放',
    tabScoreboard: 'スコア',
    flux: 'FLUX',
    unlockTree: '解放ツリー',
    unlockedCount: '{owned} / {total} 解放済',
    lifetime: '累計',

    branchArsenal: 'アーセナル',
    branchModes: 'モード',
    branchChallenge: 'チャレンジ',
    branchCosmetic: 'コスメ',

    statRunsPlayed: 'プレイ数',
    statCampaignWins: 'キャンペーン勝利',
    statBestScore: 'ベストスコア',
    statDeepestEndless: '最深 ENDLESS',
    statTotalFlux: '累計 FLUX',

    personalBests: '自己ベスト',
    waveLabel: '第 {wave} 波',

    runHistory: 'プレイ履歴',
    noRuns: 'まだ記録なし — 一局終わらせて頂上を目指そう。',
    earlierRuns: '+ {n} 件の過去記録',
    earlierRunsPlural: '+ {n} 件の過去記録',
    colRun: 'RUN',
    colMode: 'モード · 難易度',
    colResult: '結果',
    colOverload: 'OVL',
    colScore: 'スコア',
    colDeltaBest: 'Δ ベスト',
    colFlux: 'FLUX',
    starBest: '★ ベスト',

    statusUnlocked: '解放済',
    statusOwned: '保有',
    statusRequiresPrereq: 'まず {prereq} が必要',
    statusFluxShort: 'あと ✦{short} Flux',
    statusAffordable: '購入可 — クリックで解放',
    statusCost: '解放に ✦{cost}',
    buyAction: '購入  ▸',
    nodeReq: '必要: {prereq}',

    profileLauncher: 'プロフィール',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'スコア表示',
    newPersonalBest: '★  自己ベスト更新  ★',
    fromYourBest: 'ベスト比',

    grantTower: '兵器タワー',
    grantAbility: 'アクティブスキル',
    grantMode: 'ゲームモード',
    grantDifficulty: '難易度',
    grantOverload: 'オーバーロード TIER {tier}',
    grantTheme: 'ボードテーマ',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "間接射撃。経路の後方に置け — 近接はデッドゾーン；クラスター射撃、装甲削り。",
      towerSniper: "視線貫通ランス。長い直線を狙う — 線上の全敵にヒット；コーナーで弱。",
      abilityAirstrike: "対象タイルに 1 回爆撃 (45c)。クラスターを掃除か強敵を削れ。",
      abilityEmp: "全画面スタン (80c)。レーン崩壊時に全敵を瞬時凍結。",
      modeEndless: "ウェーブ 10 を超えて — コアが落ちるまで手続的ウェーブ。",
      modeMaze: "オープンフィールド建築モード。タワーはタイルを塞ぎ、敵は A*-経路で迂回。",
      difficultyHard: "難しいウェーブ；金とスコアの乗数大。",
      overloadCritical: "Overload 3 — 次ウェーブのスコア/Flux 乗数を太らせろ。",
      overloadMeltdown: "Overload 4 — 最難プリセット波。ベスト乗数。",
      themeAmber: "コスメティックパレット — 暖色 amber CRT。純コスメ；効果なし。",
      themePhosphor: "コスメティックパレット — ヴィンテージ蛍光グリーン。純コスメ；効果なし。",
    },
  },

  help: {
    eyebrow: '/ /  リ フ ァ レ ン ス',
    title: '運用マニュアル',
    controls: '操作',
    powerGrid: '電力網',
    powerGridBody:
      '兵器は電力を消費、Pylon が供給する。需要が供給を超えると電力網はブラウンアウトし全タワーが減速 — Pylon を建てて発射を維持しよう。',
    objective: '目的',
    objectiveBody:
      '15波のキャンペーンをすべて凌げば勝利;コアのライフが 0 になった瞬間に敗北。Endless モードは終わらない。',
    mazeMode: 'Maze モード',
    mazeModeBody:
      'オープンフィールドの第三モード:タワーはタイルを塞ぎ、敵は A* で迂回するため、君が迷路を組む。コアを完全に塞ぐことはできない。',
    glitches: 'グリッチ',
    towers: 'タワー',
    abilities: 'スキル',
    replayIntro: '↺  イントロ再生',
    closeHint: 'Esc / H / ? またはどこかをクリックで閉じる',
  },

  intro: {
    eyebrow: '/ /  シ ス テ ム  ブ リ ー フ',
    titleBig: 'OVERVOLT',
    subtitle: 'コアを護れ',
    line1:
      '汚染プログラムが回路をコアへ進軍中。突破される前にタワーで破壊せよ。',
    line2:
      '⚡ 各タワーは電力を消費。建て過ぎると電力網はブラウンアウト — 全タワーが一斉に減速。Pylon を建てて電力を確保。',
    line3: '静かな間に建設、ウェーブ開始で起動。15波生き延びれば勝利。',
    line4:
      'マウスで建設 · 1–5 選択 · スペース 開始/停止 · U アップグレード · S 売却',
    gotIt: '了解  →',
    gotItHint: '任意のキーで次へ',
  },

  puzzle: {
    label: 'パズル',
    levels: {
      'p0-first-light': {
        name: '初光',
        hint: 'Pulse を通路の隣に設置。射程内ならどこでも可。',
      },
      'p1-long-lance': {
        name: '長槍',
        hint: 'レーンに揃えた Sniper 一つで全敵を貫通。',
      },
      'p2-over-the-wall': {
        name: '壁越え',
        hint: '壁は直射を全遮断。迫撃弾は弧で越えていく。',
      },
      'p3-funhouse-mirror': {
        name: '歪み鏡',
        hint: '隙間を通す Sniper で両レーンを同時ヒット。',
      },
      'p4-surplus-riches': {
        name: '余剰の富',
        hint: 'フリー帯を越えて Pylon を多めに。Arc Coil は余剰で輝く。',
      },
      'p5-pinch-point': {
        name: '挟撃点',
        hint: 'Cryo で挟撃点をペイント、周囲の Pulse が群れを片付ける。',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher の試練',
        hint:
          '迫撃で装甲を削り、Cryo で曲がりをペイント、Sniper で下段脚を貫通。フル構成で挑め。',
      },
      'p7-kernel-echo': {
        name: 'Kernel のエコー',
        hint:
          '迫撃は視界壁を越えて投射。Sniper は下から通す。Power Drain に注意。',
      },
      'p8-overclock-crunch': {
        name: 'オーバークロックの瀬戸際',
        hint:
          'Pulse 一基では追いつかない。バースト窓でオーバークロック — 持続火力では足りない。',
      },
      'p9-pylon-pinch': {
        name: 'Pylon 圧縮',
        hint: 'Pylon をタワーの間に密集 — オーラ上限は密な配置を報いる。',
      },
    },
    briefing: {
      eyebrow: "パズル ブリーフィング",
      startBtn: "開始 ▷",
      cancelBtn: "キャンセル",
      sectionConstraints: "制約",
      sectionTowers: "タワー",
      sectionWinCondition: "勝利条件",
      budgetLabel: "ゴールド予算",
      wavesLabel: "ウェーブ",
      towerAllowed: "許可",
      towerForbidden: "ロック",
      resultEyebrowWon: "パズルクリア",
      resultEyebrowLost: "パズル失敗",
      resultTitleWon: "解決！",
      resultTitleLost: "もう一度",
      resultStatTowers: "タワー",
      resultStatGold: "ゴールド消費",
      resultStatLives: "残ライフ",
      resultNextBtn: "次のレベル ▷",
      resultReplayBtn: "リプレイ",
      resultResetBtn: "挑戦リセット",
      resultBackBtn: "選択に戻る",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "手作りの論理レベル — 唯一の勝ち筋を推理せよ。",
      close: "閉じる",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 ロック",
      solved: "✓ 解決",
      unsolved: "▷ 未解決",
      noSolve: "まだ未解決",
      bestLine: "ベスト：{towers}t · {gold}g · {power}p",
      allowed: "使用可能：{list}",
      parPlaceholder: "パー：— / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: '言語',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: '前回のプレイを再開しますか?',
    resume: '再開',
    newRun: '新規',
    hint: '[Enter] 再開   ·   [Esc] 新規',
    detail: "{mode} · {difficulty} · ウェーブ {wave} · タワー {towers} · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  ザ・カーネル",
    phase: "フェーズ {phase} · {name}",
    powerDrain: "⚡ パワードレイン",
    kernelIncoming: "⚠  カーネル  接近中  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "オープン S字 — 定番 OVERVOLT。" },
    cramped: { label: "Switchyard", tagline: "狭いレーン — 全タイルが争われる。" },
    lines: { label: "Long Lines", tagline: "長い直線 — ランスを通せ。" },
    serpentine: { label: "Snake Run", tagline: "タイトな蛇行 — Cryo painter が輝く。" },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "ノミナル",
      charged: "チャージド",
      surging: "サージング",
      critical: "クリティカル",
      meltdown: "メルトダウン",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "アーマードサージ" },
    fastPack: { label: "ファストパック" },
    scatteredSpread: { label: "スキャッタード" },
    streakRunners: { label: "Streak ランナー" },
    daemonRush: { label: "Daemon ラッシュ" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "カーネル" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "基本雑魚 — 安い・遅い・脆い。",
    packet: "速くて弱い；コアへ突進。",
    daemon: "重 HP スポンジ；漏れたら強打。",
    fork: "撃破時に Bit 2 体に分裂。",
    cipher: "装甲 — Mortar の腐食で切り裂け。",
    patch: "近くの Glitch を回復；最優先で潰せ。",
    buffer: "再生シールド；バースト火力で割れ。",
    streak: "単独でスプリント；スロー耐性。路をカバー。",
    kernel: "フェーズボス — 電力を吸う。次フェーズ前にバーストで仕留めろ。",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "グリッドをブースト：全タワーが短時間速射。重ウェーブに合わせて計画。",
    airstrike: "対象タイルに 1 回爆撃。クラスターをパニッククリアか強敵を削れ。",
    empBurst: "全画面スタン — 全敵を瞬間凍結。レーン崩壊時に時間稼ぎ。",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} クレジット",
    next: "NEXT  →  LV {level}   {cost} クレジット",
    cooldown: "クールダウン  {sec}s",
    stat: {
      supply: "供給  +{n}w をグリッドへ",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    チェーン {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} 体",
      slow: "SLOW ×{factor}  期間  {sec}s",
      brittle: "脆性 +{pct}% 他タワー dmg",
      pwrBlast: "PWR {power}w    爆 {tiles} タイル",
      pwrSingle: "PWR {power}w    1 体",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Pulse / Arc / Pylon / Cryo / Mortar / Sniper を選択",
    mouse: "タイルに配置 · タワーをクリックで選択",
    space: "ウェーブ開始 · ウェーブ一時停止",
    fast: "早送り (1× ⇄ 2×)",
    upgradeSell: "選択タワーをアップグレード / 売却",
    target: "選択タワーのターゲットモード切替",
    profile: "プロフィールを開く（解放ツリー · スコア）",
    utility: "キャンセル · ミュート · ラン再起動",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Endless モード" },
    modeMaze: { name: "Maze モード" },
    difficultyHard: { name: "Hard 難度" },
    overloadCritical: { name: "Overload：クリティカル" },
    overloadMeltdown: { name: "Overload：メルトダウン" },
    themeAmber: { name: "Amber ターミナル" },
    themePhosphor: { name: "蛍光グリーン" },
  },

  // Sprint 73 — インタラクティブ・チュートリアル (docs/TUTORIAL_DESIGN.md §6)。
  tutorial: {
    button: {
      start: 'チュートリアル開始',
      startSub: '初めて？ツアーをどうぞ →',
      replay: 'ツアー再生',
      replaySub: '基本をおさらい',
    },
    step: {
      welcome: {
        title: 'OVERVOLTへようこそ',
        body: 'あなたは故障したリアクターの防御AIです。破損したプログラムが回路を通じてCoreへ進軍してくる — 突破される前に破壊しよう。',
      },
      path: {
        title: '進路',
        body: '敵は常にこのS字を辿り、左のスポーンマスから右のCoreへ向かう。タレットをどこに置くかはあなた次第。',
      },
      firstPlace: {
        title: '最初のタレットを設置',
        body: 'Pulse Turretは50クレジット・電力4を要する。進路脇の光るマスをクリックして配置しよう。',
      },
      wave1: {
        title: '第1波を発進',
        body: 'スタートウェーブを押す。Pulseは射程内の敵に自動射撃する。',
      },
      towerRoles: {
        title: '5基のタワー、5つの役割',
        body: 'Pulse: どこでも頼れる。Sniper: 長い直線。Mortar: 間接砲、進路から離して。Arc: 電力余剰でスケール。Cryo: 一点を低速＋脆性化。',
      },
      powerGrid: {
        title: '電力グリッド',
        body: '各タワーは電力を消費する。需要が供給を超えるとグリッドが停電し全タワーが減速。Pylonを建てよう — 隣接するPylonは発射速度を強化。',
      },
      overloadDial: {
        title: 'Overload — リスクと報酬',
        body: '次の波のためにダイヤルを上げる：敵が強くなり、Fluxが増える。下げれば安全寄り。',
      },
      overclock: {
        title: 'タワーをオーバークロック',
        body: '選択中のタワーでOVERCLOCKを起動しバースト窓を得る：発射速度上昇、電力消費増。瞬間の勝、持続の負け。',
      },
      abilities: {
        title: 'アクティブアビリティ',
        body: '3スロット：Overchargeでグリッド全体を強化。Airstrikeで持続する焦土帯を投下。EMP Burstで全敵を麻痺。計画的に — 慌てて押さない。',
      },
      mazePreview: {
        title: 'Mazeモード',
        body: '固定進路なし — タワーはマスを塞ぎ敵はA*で回り込む。あなたが敵の通る道を作る。',
      },
      puzzlePreview: {
        title: 'Puzzleモード',
        body: '手作りのロジック面、視線壁あり。固定予算・固定波 — 唯一の勝利配置を見つけよ。別ゲーム。',
      },
      wrapUp: {
        title: '準備完了',
        body: 'ランでFLUXを獲得 — プロフィールで使ってタワー、アビリティ、モードを解禁。まずはCampaignから；Endless、Maze、Puzzleはその後で。',
      },
    },
    ui: {
      eyebrow: 'ステップ {n} / {total}',
      next: '次へ →',
      back: '← 戻る',
      skip: 'ツアーをスキップ',
      play: 'プレイ  ▸',
      skipConfirm: 'ツアーをスキップ？タイトル画面からいつでも再生できます。',
    },
  },
};
